<template>
  <div class="pdv-scanner-page-v2">
    <div class="page-header">
      <div>
        <h2>{{ t('Scanner de Produção') }}</h2>
        <p>{{ t('Escaneie o código do item para visualizar as etapas e registrar o avanço na produção.') }}</p>
      </div>
    </div>

    <ScanResultFeedback
      v-if="scanResult"
      :result="scanResult"
      @dismiss="clearScanResult"
    />

    <ScannerInputV2
      ref="scannerInputRef"
      scan-mode="manual"
      @scan="handleScan"
      @reprocess-scan="handleReprocessScan"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOScanner } from '../../composables/useSOScanner';
import { useSOProducao } from '../../composables/useSOProducao';
import { log, error as logError } from '../../utils/DebugLogger';
import ScannerInputV2 from './scanner/ScannerInputV2.vue';
import ScanResultFeedback from './scanner/ScanResultFeedback.vue';

interface ScanStepResult {
  action?: string;
  expected_step?: unknown;
  scanned_step?: unknown;
  [key: string]: unknown;
}

interface LookupItem {
  so_order_item_id?: number;
  [key: string]: unknown;
}

interface LookupStep {
  so_work_order_step_id: number;
  so_work_order_step_name?: string;
  so_work_order_step_production_step_id?: number;
  so_work_order_step_status?: string;
  [key: string]: unknown;
}

const { t } = useI18n();
const scannerInputRef = ref<InstanceType<typeof ScannerInputV2> | null>(null);
const {
  isScanning,
  scanResult,
  scanStep,
  clearScanResult,
} = useSOScanner();
const { lookupByQrcode } = useSOProducao();

const handleScan = async (stationQr: string, itemQr: string): Promise<void> => {
  log('PDVScannerPageV2', 'handleScan', 'Handling scan', { stationQr, itemQr });

  try {
    const result = await scanStep(stationQr, itemQr) as ScanStepResult | undefined;

    if (result?.action === 'out_of_order_warning') {
      eventBus.emit('modal.scanner-force.open', {
        expected_step: result.expected_step,
        scanned_step: result.scanned_step
      });

      const confirmed = await awaitForceDecision();
      if (!confirmed) {
        return;
      }

      await scanStep(stationQr, itemQr, true);
    }

    eventBus.emit('pdv.producao.refresh');
  } catch (err) {
    logError('PDVScannerPageV2', 'handleScan', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    if (message && !scanResult.value) {
      eventBus.emit('globalMessage.error', message);
    }
  }
};

const handleReprocessScan = async (itemQr: string): Promise<void> => {
  log('PDVScannerPageV2', 'handleReprocessScan', 'Resolving item for reprocesso', { itemQr });

  try {
    const lookup = await lookupByQrcode(itemQr) as { item?: LookupItem; steps?: LookupStep[] } | undefined;
    const orderItemId = lookup?.item?.so_order_item_id ?? null;

    if (!orderItemId) {
      eventBus.emit('globalMessage.error', t('Item não encontrado para reprocesso.'));
      return;
    }

    const completedSteps = (lookup?.steps ?? []).filter(
      (s: LookupStep) => s.so_work_order_step_status === 'completed'
    );

    if (completedSteps.length === 0) {
      eventBus.emit('globalMessage.error', t('Nenhum passo concluído para reprocessar neste item.'));
      return;
    }

    eventBus.emit('modal.soReprocesso.open', { orderItemId, completedSteps });
  } catch (err) {
    logError('PDVScannerPageV2', 'handleReprocessScan', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    if (message) {
      eventBus.emit('globalMessage.error', message);
    }
  }
};

const awaitForceDecision = (): Promise<boolean> => new Promise((resolve) => {
  const cleanup = (): void => {
    eventBus.off('scanner-force.confirmed', handleConfirmed);
    eventBus.off('scanner-force.cancelled', handleCancelled);
  };

  const handleConfirmed = (): void => {
    cleanup();
    resolve(true);
  };

  const handleCancelled = (): void => {
    cleanup();
    resolve(false);
  };

  eventBus.on('scanner-force.confirmed', handleConfirmed);
  eventBus.on('scanner-force.cancelled', handleCancelled);
});

const handleReprocessCreated = (): void => {
  eventBus.emit('pdv.producao.refresh');
};

onMounted(() => {
  eventBus.on('reprocesso.created', handleReprocessCreated);
});

onBeforeUnmount(() => {
  eventBus.off('reprocesso.created', handleReprocessCreated);
});
</script>

<style scoped>
.pdv-scanner-page-v2 {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.page-header h2 {
  margin: 0 0 0.35rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-header p {
  margin: 0;
  color: var(--p-text-muted-color, #6b7280);
}

.version-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: var(--p-primary-color, #2563eb);
  color: #fff;
  vertical-align: middle;
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
